import { useState, useEffect, useCallback, useRef } from "react";
import { getHocSinhByLop } from "../services/hocSinhService";
import { getDiemDanhByNgay, luuNhieuDiemDanh } from "../services/diemDanhService";
import { getCurrentSunday } from "../utils";
import { TRANG_THAI_DIEM_DANH, CHUA_DIEM_DANH } from "../constants";

// ===== CACHE =====
const CACHE_KEY = 'diemdanh_data_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 phút

const loadCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; }
  catch (e) { return {}; }
};

const saveCache = (cache) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); }
  catch (e) {}
};

export const useDiemDanh = (lopId, userId) => {
  const [hocSinh, setHocSinh] = useState([]);
  const [diemDanh, setDiemDanh] = useState({});
  const [ghiChu, setGhiChu] = useState({});
  const [diemDanhGoc, setDiemDanhGoc] = useState({});
  const [ghiChuGoc, setGhiChuGoc] = useState({});
  const [ngayChon, setNgayChon] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const cacheRef = useRef(loadCache());

  useEffect(() => { setNgayChon(getCurrentSunday()); }, []);

  const loadData = useCallback(async () => {
    if (!ngayChon || !lopId) return;

    // ===== CHECK CACHE =====
    const cacheKey = `${lopId}_${ngayChon}`;
    const cached = cacheRef.current[cacheKey];
    if (cached && cached.timestamp > Date.now() - CACHE_DURATION) {
      // Load từ cache
      setHocSinh(cached.hsList);
      setDiemDanh({ ...cached.ddMap });
      setGhiChu({ ...cached.gcMap });
      setDiemDanhGoc({ ...cached.ddMap });
      setGhiChuGoc({ ...cached.gcMap });
      setSaved(false);
      return; // Không setLoading(true) → không xoay
    }

    setLoading(true);
    try {
      const hsList = await getHocSinhByLop(lopId);
      setHocSinh(hsList);
      const ddList = await getDiemDanhByNgay(lopId, ngayChon);
      const ddMap = {}, gcMap = {};
      ddList.forEach(dd => { ddMap[dd.hocSinhId] = dd.trangThai; gcMap[dd.hocSinhId] = dd.ghiChu || ""; });
      hsList.forEach(hs => { if (!ddMap[hs.id]) ddMap[hs.id] = null; if (!gcMap[hs.id]) gcMap[hs.id] = ""; });
      setDiemDanh({ ...ddMap }); setGhiChu({ ...gcMap });
      setDiemDanhGoc({ ...ddMap }); setGhiChuGoc({ ...gcMap });
      setSaved(false);

      // Lưu cache
      cacheRef.current[cacheKey] = { hsList, ddMap, gcMap, timestamp: Date.now() };
      saveCache(cacheRef.current);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [lopId, ngayChon]);

  useEffect(() => { loadData(); }, [loadData]);

  const soChuaDiemDanh = useCallback(() => Object.values(diemDanh).filter(v => v === null).length, [diemDanh]);
  const coThayDoi = useCallback(() => {
    for (const hs of hocSinh) {
      if (diemDanh[hs.id] !== diemDanhGoc[hs.id]) return true;
      if ((ghiChu[hs.id] || "") !== (ghiChuGoc[hs.id] || "")) return true;
    }
    return false;
  }, [hocSinh, diemDanh, diemDanhGoc, ghiChu, ghiChuGoc]);

  const setTrangThai = useCallback((hsId, tt) => setDiemDanh(p => ({ ...p, [hsId]: tt })), []);
  const setGhiChuHS = useCallback((hsId, v) => setGhiChu(p => ({ ...p, [hsId]: v })), []);
  const danhDauTatCa = useCallback((tt) => { const nd = {}; hocSinh.forEach(hs => nd[hs.id] = tt); setDiemDanh(nd); }, [hocSinh]);

  const save = useCallback(async () => {
    if (hocSinh.length === 0) return { success: false, message: "Không có học sinh" };
    const chua = soChuaDiemDanh();
    if (chua > 0) return { success: false, message: "Còn " + chua + " học sinh chưa điểm danh!" };
    if (!coThayDoi()) return { success: false, message: "Không có thay đổi gì để lưu!" };
    setSaving(true);
    try {
      const ds = hocSinh.filter(hs => 
        diemDanh[hs.id] !== null && 
        (diemDanh[hs.id] !== diemDanhGoc[hs.id] || (ghiChu[hs.id] || "") !== (ghiChuGoc[hs.id] || ""))
      ).map(hs => ({ 
        hocSinhId: hs.id, lopId, ngayHoc: ngayChon, 
        trangThai: diemDanh[hs.id], 
        ghiChu: ghiChu[hs.id] || "", 
        nguoiDiemDanhId: userId 
      }));
      await luuNhieuDiemDanh(ds);
      setDiemDanhGoc({ ...diemDanh }); setGhiChuGoc({ ...ghiChu });
      setSaved(true); setTimeout(() => setSaved(false), 3000);

      // Xóa cache sau lưu
      const cacheKey = `${lopId}_${ngayChon}`;
      delete cacheRef.current[cacheKey];
      saveCache(cacheRef.current);

      return { success: true, message: "Đã lưu!" };
    } catch (e) { return { success: false, message: "Lỗi lưu!" }; }
    finally { setSaving(false); }
  }, [hocSinh, diemDanh, diemDanhGoc, ghiChu, ghiChuGoc, lopId, ngayChon, userId, coThayDoi, soChuaDiemDanh]);

  const changeDay = useCallback((delta) => {
    const p = ngayChon.split('-'), d = new Date(+p[0], +p[1]-1, +p[2]);
    d.setDate(d.getDate() + delta * 7);
    setNgayChon(d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0'));
  }, [ngayChon]);

  const veHomNay = useCallback(() => setNgayChon(getCurrentSunday()), []);
  const getCounts = useCallback(() => { const c = {}; Object.values(diemDanh).forEach(t => { if (t !== null) c[t] = (c[t]||0)+1; }); return c; }, [diemDanh]);

  return { 
    hocSinh, diemDanh, ghiChu, ngayChon, loading, saving, saved, 
    setTrangThai, setGhiChuHS, setNgayChon,
    danhDauTatCa, save, changeDay, veHomNay, 
    coThayDoi: coThayDoi(), soChuaDiemDanh: soChuaDiemDanh(),
    counts: getCounts(), TRANG_THAI: TRANG_THAI_DIEM_DANH, CHUA_DIEM_DANH 
  };
};